import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { taskKeys } from './useTasks';

export type FieldType = 'text' | 'number' | 'date' | 'checkbox';

export interface CustomFieldEntry {
  type: FieldType;
  value: string | number | boolean | null;
}

// Normaliza formato legado {key: valor} para novo formato {type, value}
export function normalizeFieldValue(raw: any): CustomFieldEntry {
  if (raw !== null && typeof raw === 'object' && 'type' in raw && 'value' in raw) {
    return raw as CustomFieldEntry;
  }
  if (typeof raw === 'boolean') return { type: 'checkbox', value: raw };
  if (typeof raw === 'number') return { type: 'number', value: raw };
  return { type: 'text', value: raw != null ? String(raw) : '' };
}

export function useTaskCustomFields(taskId: string) {
  const queryClient = useQueryClient();
  const queryKey = ['task-custom-fields', taskId];

  const { data: customFields = {} } = useQuery({
    queryKey,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tasks')
        .select('custom_fields')
        .eq('id', taskId)
        .single();
      if (error) throw error;
      return (data?.custom_fields as Record<string, any>) || {};
    },
    enabled: !!taskId,
  });

  const mutation = useMutation({
    mutationFn: async (newFields: Record<string, any>) => {
      const { error } = await supabase
        .from('tasks')
        .update({ custom_fields: newFields })
        .eq('id', taskId);
      if (error) throw error;
    },
    onMutate: async (newFields) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Record<string, any>>(queryKey);
      queryClient.setQueryData(queryKey, newFields);
      return { previous };
    },
    onError: (err: any, _, ctx) => {
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(queryKey, ctx.previous);
      }
      toast.error('Erro ao atualizar campo personalizado');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
    },
  });

  const setField = (key: string, value: any) => {
    const current = queryClient.getQueryData<Record<string, any>>(queryKey) || {};
    mutation.mutate({ ...current, [key]: value });
  };

  const removeField = (key: string) => {
    const current = queryClient.getQueryData<Record<string, any>>(queryKey) || {};
    const { [key]: _, ...rest } = current;
    mutation.mutate(rest);
  };

  return {
    customFields,
    setField,
    removeField,
    isUpdating: mutation.isPending,
  };
}
